// src/pages/RegisterWizard/steps/PersonalDetailsStep.tsx
import React, { useEffect, useState } from 'react'
import { Control, Field, Label, StepFormWrapper } from '../../styles/login.style'
import { FaCheckCircle } from 'react-icons/fa'
import { registrationData } from '../../hooks/auth.service'

// Simple regex helpers
const NAME_REGEX = /^\S+\s+\S+/
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface Props {
  data: registrationData 
  onChange: (d: registrationData) => void
  onValidityChange: (valid: boolean) => void
  isStandalone?: boolean
}

const PersonalDetailsStep: React.FC<Props> = ({
  data,
  onChange,
  onValidityChange,
  isStandalone=false,
}) => {
  const { name = '', email = '', password = ''} = data
  const [confirmPass, setConfirmPass] = useState('')

  // ─────────── Validation ───────────
  const isNameValid = NAME_REGEX.test(name.trim())
  const isEmailValid = EMAIL_REGEX.test(email.trim())
  const isPasswordValid = password!.length >= 8
  const doPasswordsMatch = password === confirmPass && password.length > 0

  // Tell parent wizard if this whole step is good
  useEffect(() => {
    if (!isStandalone) {
    onValidityChange(
      isNameValid && isEmailValid && isPasswordValid && doPasswordsMatch,
    )
  } else {
    onValidityChange(isNameValid && isEmailValid);
  }
  }, [isNameValid, isEmailValid, isPasswordValid, doPasswordsMatch, onValidityChange])

  // Icon placement constants
  const ICON_SIZE = 18
  const ICON_OFFSET = 8

  const sharedFields = (
    <>
      <Field name="name" css={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <Label>Name</Label>
        <div style={{ position: 'relative', width: '100%' }}>
          <Control
            required
            value={name}
            onChange={(e) => onChange({ ...data, name: e.currentTarget.value })}
            placeholder="First Last"
            css={{
              width: '100%',
              paddingRight: `${ICON_SIZE + ICON_OFFSET * 2}px`,
              border: isNameValid ? '2px solid green' : undefined,
            }}
          />
          {isNameValid && ( <FaCheckCircle fill="green" size={ICON_SIZE} style={{ position: 'absolute', right: ICON_OFFSET, top: '50%', transform: 'translateY(-50%)' }} /> )}
        </div>
      </Field>

      <Field name="email" css={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <Label>Email</Label>
        <div style={{ position: 'relative', width: '100%' }}>
          <Control
            required
            type="email"
            value={email}
            disabled={isStandalone} // Email is editable in register, but not on profile page
            onChange={(e) => onChange({ ...data, email: e.currentTarget.value })}
            placeholder="you@example.com"
            css={{
              width: '100%',
              paddingRight: `${ICON_SIZE + ICON_OFFSET * 2}px`,
              border: isEmailValid ? '2px solid green' : undefined,
            }}
          />
          {isEmailValid && ( <FaCheckCircle fill="green" size={ICON_SIZE} style={{ position: 'absolute', right: ICON_OFFSET, top: '50%', transform: 'translateY(-50%)' }}/> )}
        </div>
      </Field>
    </>
  );


  if (isStandalone) {
    return sharedFields 
  }
  return (
    <>
    <StepFormWrapper>
    {sharedFields}



<Field name="password" css={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
  <Label>Password</Label>
  <div style={{ position: 'relative', width: '100%' }}>
    <Control
      required
      type="password"
      value={password}
      onChange={(e) => onChange({ ...data, password: e.currentTarget.value })}
      placeholder="At least 8 characters"
      css={{
        width: '100%',
        paddingRight: `${ICON_SIZE + ICON_OFFSET * 2}px`,
        border: isPasswordValid ? '2px solid green' : undefined,
      }}
    />
    {isPasswordValid && (
      <FaCheckCircle
        fill="green"
        size={ICON_SIZE}
        style={{ position: 'absolute', right: ICON_OFFSET, top: '50%', transform: 'translateY(-50%)' }}
      />
    )}
  </div>
</Field>

{/* ─────────── Confirm ─────────── */}
<Field name="confirm" css={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
  <Label>Confirm Password</Label>
  <div style={{ position: 'relative', width: '100%' }}>
    <Control
      required
      type="password"
      value={confirmPass}
      onChange={(e) => setConfirmPass(e.currentTarget.value)}
      placeholder="Re-enter password"
      css={{
        width: '100%',
        paddingRight: `${ICON_SIZE + ICON_OFFSET * 2}px`,
        border: doPasswordsMatch ? '2px solid green' : undefined,
      }}
    />
    {doPasswordsMatch && (
      <FaCheckCircle
        fill="green"
        size={ICON_SIZE}
        style={{ position: 'absolute', right: ICON_OFFSET, top: '50%', transform: 'translateY(-50%)' }}
      />
    )}
  </div>
</Field>
</StepFormWrapper>


    </>
  )
}

export default PersonalDetailsStep
